/*
 * Signing in: one mobile number, one code, no password.
 *
 * WHY THIS MODULE IS A LUXURY AND NOT A DEPENDENCY
 * ------------------------------------------------
 * `www/account/login.py` runs the whole three-step state machine as ordinary form
 * POSTs to `/account/login`, calling the SAME whitelisted endpoints this file calls
 * (`request_otp`, `resend_otp`, `verify_otp`), in-process. Signing in is the one
 * thing on this storefront that cannot be worked around — a parent who cannot log in
 * cannot place an order, see their token or track a collection — so it may not
 * depend on a script. Everything here saves a page load and nothing here is load
 * bearing, which is why every failure path below ends in `submitNatively()`: the
 * server flow takes over exactly where this left off.
 *
 * WHAT IS NOT DECIDED HERE
 * ------------------------
 * Whether a number exists, whether an account gets created, whether a staff mobile
 * is refused, and what any of that is called on the screen. `api.auth` answers a
 * live number, an unknown number, a staff number, a capped number and a suppressed
 * send with the IDENTICAL envelope on purpose (docs/BUILD_DECISIONS.md sections 2
 * and 3), and this module draws one "we sent it" screen for all five. There is no
 * branch in this file that a stranger could time or read.
 *
 * The messages shown on failure are the envelope's own — copy `api.auth` wrote for a
 * customer — never a status code turned into a sentence here.
 *
 * WHY SIX BOXES
 * -------------
 * `Splashbox Settings.otp_length` is deliberately kept out of the public settings
 * payload: knowing the code length and the attempt cap is knowing what a brute force
 * would cost (`api/settings.py`), and `login.py` only draws the boxes for someone who
 * has just been sent a code. The AJAX path has no way to learn it, so it draws the
 * DocType's default — the same `DEFAULT_OTP_LENGTH` the controller falls back to. If
 * a shop ever sets a different length the boxes still work: `verify_otp` accepts
 * whatever was typed, only the "all boxes full, submit it for them" shortcut goes
 * quiet, and the next server-rendered page draws the right number.
 */

import { ApiError, METHODS, post } from "./api.js";
import { all, empty, icon, one, safePath, span } from "./nav.js";

/** Matches `www/account/login.py:DEFAULT_OTP_LENGTH` and the approved design. */
const DEFAULT_OTP_LENGTH = 6;

/** How `login.py:CHANNEL_LABELS` writes the channel `api.auth` reports. */
const CHANNELS = { whatsapp: "WhatsApp", sms: "SMS" };

/* ── the mobile step ───────────────────────────────────────────────────────── */

/**
 * Ask for a code without leaving the page.
 *
 * The purpose follows the pane the customer is standing in, exactly as the
 * controller does. It only steers the wording of the message they receive — whether
 * an account is created is decided server-side at verify time and never by this
 * argument (section 2).
 */
function bindSend(form, purpose) {
	form.addEventListener("submit", function (event) {
		if (form.hasAttribute("data-sb-native")) return;

		const mobile = value(form, "mobile");
		if (!mobile) return; // `required` on the field; let the browser say so.

		event.preventDefault();

		const button = one('button[value="send"]', form);
		const restore = busy(button, "Sending…");

		post(METHODS.requestOtp, { mobile: mobile, purpose: purpose })
			.then(function (data) {
				restore();
				renderCodeStage(form, data || {}, purpose);
			})
			.catch(function (error) {
				restore();
				if (!(error instanceof ApiError)) throw error;
				// The server flow answers this properly, including the rate limiter's
				// own 429 page, so hand it over rather than inventing a sentence.
				submitNatively(form, "send");
			});
	});
}

/* ── the code step ─────────────────────────────────────────────────────────── */

/**
 * Turn the mobile form into the code form, in place.
 *
 * The same `<form>` element is reused so its `method`, `action` and CSRF token
 * survive: if the customer's code is wrong three times and something here gives up,
 * `submitNatively()` still posts a real, complete form to `/account/login`.
 */
function renderCodeStage(form, data, purpose) {
	const csrf = value(form, "csrf_token");
	const redirect = value(form, "redirect-to") || value(form, "redirect_to") || "/";
	const fullName = value(form, "full_name");
	const boxes = existingBoxCount() || DEFAULT_OTP_LENGTH;

	empty(form);
	form.removeAttribute("data-sb-login-form");
	form.removeAttribute("data-sb-signup-form");
	form.setAttribute("data-sb-verify-form", "");

	hidden(form, "csrf_token", csrf);
	hidden(form, "request_id", data.request_id || "");
	hidden(form, "redirect-to", redirect);
	hidden(form, "tab", purpose === "signup" ? "signup" : "login");
	if (fullName) hidden(form, "full_name", fullName);

	const sent = document.createElement("div");
	sent.className = "sb-meta";
	sent.appendChild(document.createTextNode("We sent a code"));
	const channel = CHANNELS[String(data.channel || "").toLowerCase()];
	if (channel) sent.appendChild(document.createTextNode(" on " + channel));
	if (data.masked) {
		sent.appendChild(document.createTextNode(" to "));
		sent.appendChild(span("sb-mono", data.masked));
	}
	sent.appendChild(document.createTextNode("."));
	form.appendChild(sent);

	const otp = document.createElement("div");
	otp.className = "sb-otp";
	for (let index = 0; index < boxes; index += 1) {
		const box = document.createElement("input");
		box.type = "text";
		box.name = "code";
		box.maxLength = 1;
		box.setAttribute("inputmode", "numeric");
		box.setAttribute("pattern", "[0-9]*");
		box.setAttribute("aria-label", "Digit " + (index + 1));
		box.setAttribute("autocomplete", index === 0 ? "one-time-code" : "off");
		box.setAttribute("data-sb-otp-box", "");
		otp.appendChild(box);
	}
	form.appendChild(otp);

	const acts = document.createElement("div");
	acts.className = "sb-acts2";

	const verify = document.createElement("button");
	verify.className = "sb-pill-btn sb-pri";
	verify.type = "submit";
	verify.name = "action";
	verify.value = "verify";
	verify.appendChild(icon("check"));
	verify.appendChild(document.createTextNode("Verify & continue"));
	acts.appendChild(verify);

	const resend = document.createElement("button");
	resend.className = "sb-pill-btn sb-ter";
	resend.type = "submit";
	resend.name = "action";
	resend.value = "resend";
	resend.setAttribute("data-sb-resend", "");
	if (data.resend_after) resend.setAttribute("data-sb-resend-after", String(data.resend_after));
	resend.textContent = "Resend the code";
	acts.appendChild(resend);

	form.appendChild(acts);

	const help = document.createElement("p");
	help.className = "sb-cap";
	help.textContent =
		"Didn't get it? Wait a few seconds, then tap Resend the code. Codes expire quickly — " +
		"asking for a new one is always safe.";
	form.appendChild(help);

	bindCodeForm(form);
	const first = one("[data-sb-otp-box]", form);
	if (first) first.focus();
}

/** How many boxes the server drew, when it drew any. */
function existingBoxCount() {
	return all("[data-sb-otp-box]").length;
}

/**
 * Everything the code screen does: type, paste, verify, resend.
 *
 * Bound to whichever form is in front of the customer — the one the server rendered
 * after a plain POST, or the one `renderCodeStage` just built.
 */
function bindCodeForm(form) {
	if (form.hasAttribute("data-sb-bound")) return;
	form.setAttribute("data-sb-bound", "1");

	const boxes = all("[data-sb-otp-box]", form);
	if (boxes.length) bindBoxes(form, boxes);

	bindResend(form);

	form.addEventListener("submit", function (event) {
		if (form.hasAttribute("data-sb-native")) return;
		event.preventDefault();
		verify(form);
	});

	form.addEventListener("click", function (event) {
		const button = event.target.closest("button");
		if (!button || button.getAttribute("name") !== "action") return;
		event.preventDefault();
		if (button.value === "resend") resend(form, button);
		else verify(form);
	});
}

/**
 * One box per digit, behaving the way a person expects.
 *
 * The awkward cases are the ones that matter: an SMS autofill that drops all six
 * characters into the first box, a paste of "123 456" from a WhatsApp message, and
 * backspace at the start of an empty box. Each of those, mishandled, ends with a
 * customer being told their own correct code is wrong.
 */
function bindBoxes(form, boxes) {
	function fill(from, digits) {
		for (let index = from; index < boxes.length && digits.length; index += 1) {
			boxes[index].value = digits.charAt(0);
			digits = digits.slice(1);
		}

		// The next box still waiting for a digit, searching FORWARD from where the
		// customer was: pasting the whole code lands on the last box, typing one
		// digit lands on the next one, and filling a gap in the middle does not
		// throw the cursor back to the start.
		let target = null;
		for (let index = from; index < boxes.length; index += 1) {
			if (!boxes[index].value) {
				target = boxes[index];
				break;
			}
		}
		(target || boxes[boxes.length - 1]).focus();

		maybeVerify(form, boxes);
	}

	boxes.forEach(function (box, index) {
		box.addEventListener("input", function () {
			const digits = String(box.value || "").replace(/\D+/g, "");
			if (!digits) {
				box.value = "";
				return;
			}
			box.value = "";
			fill(index, digits);
		});

		box.addEventListener("keydown", function (event) {
			if (event.key === "Backspace" && !box.value && index > 0) {
				event.preventDefault();
				boxes[index - 1].value = "";
				boxes[index - 1].focus();
			} else if (event.key === "ArrowLeft" && index > 0) {
				event.preventDefault();
				boxes[index - 1].focus();
			} else if (event.key === "ArrowRight" && index < boxes.length - 1) {
				event.preventDefault();
				boxes[index + 1].focus();
			}
		});

		box.addEventListener("paste", function (event) {
			const clipboard = event.clipboardData || window.clipboardData;
			if (!clipboard) return;
			const digits = String(clipboard.getData("text") || "").replace(/\D+/g, "");
			if (!digits) return;
			event.preventDefault();
			fill(index, digits);
		});

		box.addEventListener("focus", function () {
			box.select();
		});
	});
}

/** Every box full is an unambiguous "go" — nobody types a seventh digit. */
function maybeVerify(form, boxes) {
	const complete = boxes.every(function (box) {
		return String(box.value || "").length === 1;
	});
	if (complete) verify(form);
}

/** The typed code, joined the way `login.py:_code` joins it. */
function readCode(form) {
	const boxes = all("[data-sb-otp-box]", form);
	const source = boxes.length ? boxes : all('input[name="code"]', form);
	return source
		.map(function (box) {
			return String(box.value || "");
		})
		.join("")
		.replace(/\s+/g, "");
}

function verify(form) {
	if (form.hasAttribute("data-sb-verifying")) return;

	const code = readCode(form);
	const requestId = value(form, "request_id");
	if (!code || !requestId) {
		const first = one("[data-sb-otp-box]", form);
		if (first) first.focus();
		return;
	}

	form.setAttribute("data-sb-verifying", "1");
	const button = one('button[value="verify"]', form);
	const restore = busy(button, "Checking…");

	post(METHODS.verifyOtp, {
		request_id: requestId,
		code: code,
		redirect_to: value(form, "redirect-to") || value(form, "redirect_to") || "/",
		full_name: value(form, "full_name") || null
	})
		.then(function (data) {
			// Signed in. `api.auth._safe_redirect` has already refused anything that
			// is not a same-site path; this checks again because the value reaches
			// `location.assign` from here.
			window.location.assign(safePath(data && data.redirect_to) || "/");
		})
		.catch(function (error) {
			form.removeAttribute("data-sb-verifying");
			restore();
			if (!(error instanceof ApiError)) throw error;

			// A wrong code, a locked identity and an expired request all arrive with a
			// sentence `api.auth` wrote. Anything else hands over to the server flow,
			// which knows how to answer it.
			if (error.fromEnvelope) {
				showError(form, error.message);
				clearBoxes(form);
			} else {
				submitNatively(form, "verify");
			}
		});
}

function clearBoxes(form) {
	const boxes = all("[data-sb-otp-box]", form);
	boxes.forEach(function (box) {
		box.value = "";
	});
	if (boxes.length) boxes[0].focus();
}

/* ── resend, and the wait before it ────────────────────────────────────────── */

/**
 * The cooldown is the server's number, counted down here.
 *
 * `otp.issue()` owns the real rule and its answer is better than a refusal: inside
 * the window it hands back the code that is already on its way, with `resend_after`
 * counting down the remainder. So this is honest feedback about a wait that exists,
 * not a second implementation of the cap.
 */
function bindResend(form) {
	const button = one("[data-sb-resend]", form);
	if (!button) return;
	const wait = parseInt(button.getAttribute("data-sb-resend-after"), 10) || 0;
	if (wait > 0) cooldown(button, wait);
}

function cooldown(button, seconds) {
	const label = button.getAttribute("data-sb-label") || button.textContent;
	button.setAttribute("data-sb-label", label);
	button.disabled = true;

	let left = seconds;
	function tick() {
		if (left <= 0) {
			button.disabled = false;
			button.textContent = label;
			return;
		}
		button.textContent = "Resend in " + left + "s";
		left -= 1;
		setTimeout(tick, 1000);
	}
	tick();
}

function resend(form, button) {
	if (button.disabled) return;

	const requestId = value(form, "request_id");
	if (!requestId) {
		submitNatively(form, "resend");
		return;
	}

	const restore = busy(button, "Sending…");
	post(METHODS.resendOtp, { request_id: requestId })
		.then(function (data) {
			restore();
			// A resend inside the cooldown returns the SAME request_id and the code
			// already on its way; a real resend returns a new one. Either way the
			// row this form now points at is the one the server just named.
			hidden(form, "request_id", (data && data.request_id) || requestId);
			showError(form, "");
			clearBoxes(form);
			cooldown(button, (data && data.resend_after) || 30);
		})
		.catch(function (error) {
			restore();
			if (!(error instanceof ApiError)) throw error;
			if (error.fromEnvelope) showError(form, error.message);
			else submitNatively(form, "resend");
		});
}

/* ── shared bits ───────────────────────────────────────────────────────────── */

/** Read one field's value off the form, whatever kind of input it is. */
function value(form, name) {
	const field = form.elements ? form.elements[name] : null;
	if (!field) return "";
	// A repeated name (the six `code` boxes) gives a RadioNodeList, not an input.
	if (field.value === undefined) return "";
	return String(field.value || "").trim();
}

/** Set or create a hidden field. */
function hidden(form, name, fieldValue) {
	let field = form.querySelector('input[type="hidden"][name="' + name + '"]');
	if (!field) {
		field = document.createElement("input");
		field.type = "hidden";
		field.name = name;
		form.appendChild(field);
	}
	field.value = fieldValue || "";
	return field;
}

/** Disable a button and say what is happening. Returns the undo. */
function busy(button, label) {
	if (!button) {
		return function () {};
	}
	const original = button.cloneNode(true);
	button.disabled = true;
	empty(button);
	button.appendChild(document.createTextNode(label));
	return function () {
		button.disabled = false;
		empty(button);
		while (original.firstChild) button.appendChild(original.firstChild);
	};
}

/**
 * The one alert line on this form, in the shape `login.html` renders it.
 *
 * `message` is always copy that came out of the envelope — never a status code, and
 * never anything this file composed about why a code was refused.
 */
function showError(form, message) {
	let host = one(".sb-inset.sb-amb", form);

	if (!message) {
		if (host && host.parentNode) host.parentNode.removeChild(host);
		return;
	}

	if (!host) {
		host = document.createElement("div");
		host.className = "sb-inset sb-amb";
		host.setAttribute("role", "alert");
		const otp = one(".sb-otp", form);
		form.insertBefore(host, otp || null);
	}

	empty(host);
	host.appendChild(icon("info"));
	host.appendChild(span("", message));
}

/**
 * Hand this form back to the browser and the server-side state machine.
 *
 * `action` has to be injected first: `form.submit()` does not include the submit
 * button that was pressed, and `login.py:_handle_post` reads `action` to decide
 * which step to run — without it a POST renders step one and the customer starts
 * over. `data-sb-native` stops the listeners above from catching the submit they
 * are about to cause.
 */
function submitNatively(form, action) {
	hidden(form, "action", action);
	form.setAttribute("data-sb-native", "1");
	form.submit();
}

/* ── boot ──────────────────────────────────────────────────────────────────── */

export function init() {
	const login = one("[data-sb-login-form]");
	if (login) bindSend(login, "login");

	const signup = one("[data-sb-signup-form]");
	if (signup) bindSend(signup, "signup");

	const verifyForm = one("[data-sb-verify-form]");
	if (verifyForm) {
		bindCodeForm(verifyForm);
		const first = one("[data-sb-otp-box]", verifyForm);
		if (first && !first.value) first.focus();
	}
}
